import { useContext } from "react";

import { Skeleton } from "@upstageai/ui";

import { EndpointType } from "../../constants/Endpoints";
import {
  ViewType,
  getEndpointViewComponents,
} from "../../constants/EndpointViews";
import { ValidatorFileContext } from "../../hooks/useFile";
import { endpointStore } from "../../utils/mobx/EndpointStore";

export const ResultViewSkeleton = () => {
  const renderTextBlock = () => (
    <div className="space-y-2">
      <Skeleton className="h-6 w-1/3" /> {/* 섹션 제목 */}
      <div className="space-y-1.5">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-[85%]" />
        <Skeleton className="h-4 w-[90%]" />
      </div>
    </div>
  );

  const renderImageBlock = () => (
    <div className="space-y-2">
      <Skeleton className="h-6 w-1/4" /> {/* 이미지 캡션 */}
      <div className="flex gap-4">
        <Skeleton className="h-40 w-[80%] rounded-md" /> {/* 이미지 */}
      </div>
    </div>
  );

  const renderTableBlock = () => (
    <div className="space-y-2">
      <Skeleton className="h-6 w-1/3" /> {/* 테이블 제목 */}
      <div className="rounded-md border border-border">
        {/* 테이블 헤더 */}
        <div className="flex border-b border-border p-3">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={`header-${i}`} className="mx-2 h-5 flex-1" />
          ))}
        </div>
        {/* 테이블 바디 */}
        {[...Array(3)].map((_, rowIndex) => (
          <div
            key={`row-${rowIndex}`}
            className="flex border-b border-border/50 p-3 last:border-0"
          >
            {[...Array(4)].map((_, colIndex) => (
              <Skeleton
                key={`cell-${rowIndex}-${colIndex}`}
                className="mx-2 h-4 flex-1"
                style={{ opacity: 0.8 - rowIndex * 0.1 }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="h-full w-full overflow-auto bg-white p-6">
      <div className="mx-auto max-w-4xl space-y-8">
        {/* 컨텐츠 블록들 */}
        <div className="space-y-10">
          {/* 텍스트 블록 */}
          {renderTextBlock()}

          {/* 이미지와 설명 블록 */}
          {renderImageBlock()}

          {/* 또 다른 텍스트 블록 */}
          {renderTextBlock()}

          {/* 테이블 블록 */}
          {renderTableBlock()}

          {/* 마지막 텍스트 블록 */}
          {renderTextBlock()}
        </div>
      </div>
    </div>
  );
};

// 결과 뷰 컴포넌트
export const ResultView = ({
  view,
  dragging,
}: {
  view: ViewType;
  dragging: boolean;
}) => {
  const { endpointType } = endpointStore;
  const { inferenceState } = useContext(ValidatorFileContext);

  const endpoint = endpointType as EndpointType;
  const components = getEndpointViewComponents(dragging);
  const component = components[endpoint]?.[view];

  if (inferenceState.isInfering) return <ResultViewSkeleton />;

  return component ?? null;
};
